import axios from 'axios';
import { DateTime } from 'luxon';
import { action, computed, makeObservable, reaction } from 'mobx';

import { timelogsApi } from '@/api/timelogsApi';
import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import { UserStore } from '@/configs/store';
import { AlertsStore } from '@/configs/store/AlertsStore';
import { QueryStore } from '@/configs/store/QueryStore';
import { ViewTable } from '@/configs/ViewTable';
import { GlobalUserFilterModel } from '@/features/globalUserFilter';
import { fetchUserLogs, LogsMode } from '@/features/logsMode';
import { viewSettingsStore } from '@/features/viewSettings';
import { DateNavigationModel } from '@/models/DateNavigationModel';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { SuggestionsModel } from '@/models/SuggestionsModel';
import { ValueModel } from '@/models/ValueModel';
import { ILocalStore } from '@/types/store';
import { YandexTask } from '@/types/yandex';
import { extractTaskKeyFromUrl } from '@/utils/extractTaskKeyFromUrl';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { formatError } from '@/utils/formatError';
import { getCalendarRange } from '@/utils/getCalendarRange';
import { transformTimelogsTask } from '@/utils/transformTimelog';

import { type TimesheetData, WeeklyWorkLog } from '../types';

import { FiltersModel } from './filters';
import { LogOperationsDependencies, LogOperationsModel } from './log/LogOperationsModel';

const TASK_KEY = /^[A-Za-z][\w]+-\d+$/;

export class TimeLogStore extends LocalStore implements ILocalStore {
  private readonly _userStore: UserStore;
  private readonly _usersFilterModel: GlobalUserFilterModel;
  private readonly _queryStore: QueryStore;
  private readonly _alertsStore: AlertsStore;
  private readonly _viewSettingsStore = viewSettingsStore;
  readonly mode: LogsMode;

  private _reloadTrigger = new ValueModel<number>(0);
  private readonly _timesheetData = new ValueModel<TimesheetData | null>(null);
  private readonly _filterData = new ValueModel<TimesheetData | null>(null);

  readonly dateNavigation: DateNavigationModel;
  readonly filters: FiltersModel;

  readonly loadingStage = new LoadingStageModel();
  // Проксируем deleteStage из logOperations

  private readonly _suggestionsModel: SuggestionsModel<YandexTask>;
  private _lastWeek = new ValueModel<DateTime>(DateTime.local());
  private readonly _lastAddId = new ValueModel<string | null>(null);
  private readonly _disabledLogId = new ValueModel<string | null>(null);

  readonly logOperations: LogOperationsModel;

  private abortController?: AbortController | null;

  constructor({
    userStore,
    usersFilterModel,
    alertsStore,
    queryStore,
    mode,
  }: {
    userStore: UserStore;
    usersFilterModel: GlobalUserFilterModel;
    alertsStore: AlertsStore;
    queryStore: QueryStore;
    mode: LogsMode;
  }) {
    super();

    this.mode = mode;

    this._userStore = userStore;
    this._usersFilterModel = usersFilterModel;
    this._alertsStore = alertsStore;
    this._queryStore = queryStore;

    this.dateNavigation = new DateNavigationModel(
      DateTime.local().startOf(viewSettingsStore.view),
      {
        getView: () => this._viewSettingsStore.view,
        setView: (view) => this._viewSettingsStore.setView(view),
      },
    );

    this.filters = new FiltersModel({
      getTimesheetData: () => this._timesheetData.value,
      getCurrentDate: () => this.currentDate,
      getView: () => this._viewSettingsStore.view,
      setFilterData: (data) => this.setFilterData(data),
    });

    this._suggestionsModel = new SuggestionsModel<YandexTask>({
      fetchFn: async ({ query, signal }) => {
        if (!this.userStore.isAuthorized || !query.trim()) return [];

        const resolved = extractTaskKeyFromUrl(query.trim());

        try {
          if (this.mode === LogsMode.v2) {
            return await this._suggestTasksV2({ query: resolved, signal });
          }

          // If resolved query looks like a task key (e.g. "QUEUE-123"), fetch task directly
          if (/^[A-Za-z][\w]+-\d+$/.test(resolved)) {
            try {
              const task = await yandexTrackerApi.getTask({ taskKey: resolved });

              return [task as YandexTask];
            } catch {
              // Task not found by key — fall through to suggest
            }
          }

          return await yandexTrackerApi.searchAllTasks(resolved, signal);
        } catch (e) {
          console.error('Ошибка поиска задач', e);
          const error = formatError(e);

          if (axios.isCancel(e)) return [];

          this._alertsStore.addAlert({
            message: error.message,
            title: error.title,
            withReload: false,
            autoClose: true,
          });

          return [];
        }
      },
    });

    const dependencies: LogOperationsDependencies = {
      mode,
      getCurrentTimesheetData: async (): Promise<TimesheetData | null> => {
        if (this._timesheetData.value && !this.loadingStage.isLoading) {
          return this._timesheetData.value;
        }

        // Иначе ждём, пока loadData завершится
        return new Promise((resolve) => {
          const disposer = reaction(
            () => this._timesheetData.value,
            (data) => {
              if (data) {
                disposer(); // отписываемся
                resolve(data);
              }
            },
          );
        });
      },
      updateTimesheetData: (data) => {
        this._timesheetData.change(data);

        if (this.filters.taskKeys.selectedKeys.length > 0) {
          this.filters.taskKeys.apply();
        } else {
          this._filterData.change(data);
        }
      },
      getCurrentPeriodStartISO: () => this.periodStartISO,
      setCurrentDate: (date) => this.setCurrentDate(date),
      setLastAddId: (id) => this.setLastAddId(id),
      getUser: () => this.user,
      getSelectedUser: () => this.usersFilterModel.selectedUser,
      setDisabledLogId: (id) => this.setDisabledLogId(id),
      addAlert: (alert) => this._alertsStore.addAlert(alert),
      addQueueFromTaskKey: (taskKey) => this.filters.taskKeys.addQueueFromTaskKey(taskKey),
      removeQueueIfNotExists: (queue) => this.filters.taskKeys.removeQueueIfNotExists(queue),
    };

    this.logOperations = new LogOperationsModel(dependencies);

    makeObservable(this, {
      user: computed,
      selectedUserId: computed,
      timesheetData: computed,
      currentDate: computed,
      periodStartISO: computed,
      lastAddId: computed,
      lastWeek: computed,
      disabledLogId: computed,
      deleteStage: computed,
      filterData: computed,
      setDisabledLogId: action.bound,
      setTimesheetData: action.bound,
      updateTimesheetData: action.bound,
      setCurrentDate: action.bound,
      setLastWeek: action.bound,
      loadData: action.bound,
      syncDateToUrl: action.bound,
    });

    this.loadQueryParams();

    const userReaction = reaction(
      () => this.usersFilterModel.selectedUserId,
      (userId) => {
        if (!userId) {
          this._timesheetData.change(null);

          return;
        }

        const dateISO = this.currentDate.toISODate();

        this.loadData(dateISO);
      },
      { fireImmediately: true },
    );

    const dateReaction = reaction(
      () => [this.dateNavigation.currentDate.toISODate(), this._viewSettingsStore.view],
      ([dateISO], [prevDateISO]) => {
        if (dateISO !== prevDateISO) {
          this.syncDateToUrl(this.dateNavigation.currentDate);
          this.loadData(dateISO);
        }
      },
    );

    const reloadReaction = reaction(
      () => this._reloadTrigger.value,
      () => {
        const dateISO = this.currentDate.toISODate();
        this.loadData(dateISO);
      },
    );

    this.addReactions([userReaction, dateReaction, reloadReaction]);
  }

  get deleteStage() {
    return this.logOperations.deleteStage;
  }

  get lastAddId() {
    return this._lastAddId.value;
  }

  get disabledLogId() {
    return this._disabledLogId.value;
  }

  setDisabledLogId(id: string | null) {
    this._disabledLogId.change(id);
  }

  setLastAddId(id: string | null) {
    this._lastAddId.change(id);
  }

  get filterData() {
    return this._filterData.value;
  }

  setFilterData(data: TimesheetData | null) {
    this._filterData.change(data);
  }

  get lastWeek() {
    return this._lastWeek.value;
  }

  get currentDate() {
    return this.dateNavigation.currentDate;
  }

  setCurrentDate(date: DateTime) {
    this.dateNavigation.setCurrentDate(date);
    this.syncDateToUrl(date);
  }

  syncDateToUrl(date: DateTime) {
    const view = this._viewSettingsStore.view;

    if (view === ViewTable.Month) {
      const calendarRange = getCalendarRange(date);

      if (!calendarRange) return;

      this.queryStore.setParam('date_from', calendarRange.startOfCalendar.toISODate());
      this.queryStore.setParam('date_to', calendarRange.endOfCalendar.toISODate());
    } else {
      const startOfWeek = date.startOf('week');
      const endOfWeek = startOfWeek.plus({ days: 6 }).endOf('day');
      this.queryStore.setParam('date_from', startOfWeek.toISODate());
      this.queryStore.setParam('date_to', endOfWeek.toISODate());
    }
  }

  setLastWeek(date: DateTime) {
    this._lastWeek.change(date);
  }

  get periodStartISO() {
    return this.currentDate.startOf(viewSettingsStore.view).toISODate()!;
  }

  get userStore() {
    return this._userStore;
  }

  get queryStore() {
    return this._queryStore;
  }

  get usersFilterModel() {
    return this._usersFilterModel;
  }

  get suggestions() {
    return this._suggestionsModel.suggestions;
  }

  get suggestQuery() {
    return this._suggestionsModel.suggestQuery;
  }

  get timesheetData() {
    return this._timesheetData.value;
  }

  get user() {
    return this.userStore.user;
  }

  get selectedUserId() {
    return this.usersFilterModel.selectedUserId;
  }

  triggerReload = () => {
    this._reloadTrigger.change(this._reloadTrigger.value + 1);
  };

  setSuggestQuery = (query: string) => {
    this._suggestionsModel.setSuggestQuery(query);
  };

  setTimesheetData(data: TimesheetData | null) {
    this._timesheetData.change(data);
  }

  updateTimesheetData(data: TimesheetData | null) {
    this._timesheetData.change(data);

    if (this.filters.taskKeys.selectedKeys.length > 0) {
      this.filters.taskKeys.apply();
    } else {
      this._filterData.change(data);
    }
  }

  async loadData(viewStartISO: string | null = null) {
    if (!this.userStore.user) return;

    this.filters.taskKeys.setSelectedKeys([]);

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    try {
      this.loadingStage.loading();

      const targetUserId = this.selectedUserId ?? this.userStore.user?.id;

      const view = this._viewSettingsStore.view;
      const today = DateTime.local();

      let startDate: DateTime;
      let endDate: DateTime;

      const startDateMonth = this.currentDate.startOf(view);

      if (view === ViewTable.Week) {
        startDate = viewStartISO ? DateTime.fromISO(viewStartISO) : today.startOf('week');
        endDate = startDate.plus({ days: 6 }).endOf('day');
      } else {
        const baseDate = viewStartISO ? DateTime.fromISO(viewStartISO) : today;
        const calendarRange = getCalendarRange(baseDate);

        if (!calendarRange) return;

        startDate = calendarRange.startOfCalendar;
        endDate = calendarRange.endOfCalendar;
      }

      const workLogs = await fetchUserLogs({
        mode: this.mode,
        userId: targetUserId!,
        from: startDate.toISO()!,
        to: endDate.toISO()!,
        signal,
        view,
      });

      const days: Record<string, WeeklyWorkLog[]> = {};
      let totalMinutes = 0;
      const uniqueQueues = new Set<string>();

      for (let dt = startDate; dt <= endDate; dt = dt.plus({ days: 1 })) {
        const dateStr = dt.toISODate();

        if (dateStr) days[dateStr] = [];
      }

      workLogs.forEach((weeklyLog) => {
        const logDT = DateTime.fromISO(weeklyLog.start);

        const queue = this.filters.taskKeys.extractQueueFromTaskKey(weeklyLog.taskKey);

        if (queue) {
          uniqueQueues.add(queue);
        }

        if (days[weeklyLog.date]) {
          days[weeklyLog.date].push(weeklyLog);
        }

        if (view === ViewTable.Week || logDT.month === startDateMonth.month) {
          totalMinutes += weeklyLog.durationMinutes;
        }
      });
      const totalFormatted = formatDurationRussian(totalMinutes);

      const initialData = {
        weekStart: startDate.toISODate()!,
        weekEnd: endDate.toISODate()!,
        days,
        totalMinutes,
        totalFormatted,
      };

      this._timesheetData.change(initialData);
      this.setFilterData(initialData);

      const sortedQueues = Array.from(uniqueQueues).sort();
      this.filters.taskKeys.setAvailableKeys(sortedQueues);

      this.loadingStage.success();
    } catch (e) {
      if (axios.isAxiosError(e) && e.code === 'ERR_CANCELED') return;

      this.loadingStage.error();
      const error = formatError(e);

      this._alertsStore.addAlert({
        message: error.message,
        title: error.title,
        withReload: false,
        autoClose: true,
      });
    }
  }

  /** Ключ задачи — сама задача, иначе подсказки; задачи только из нашей БД */
  private async _suggestTasksV2({
    query,
    signal,
  }: {
    query: string;
    signal: AbortSignal;
  }): Promise<YandexTask[]> {
    if (TASK_KEY.test(query)) {
      try {
        return [transformTimelogsTask(await timelogsApi.getTask(query))];
      } catch {
        // Задачи с таким ключом нет — ищем подсказками
      }
    }

    const tasks = await timelogsApi.suggestTasks({ query, signal });

    return tasks.map(transformTimelogsTask);
  }

  private loadQueryParams() {
    const paramDate = this.queryStore.getParam('date_from');
    let date: DateTime;

    if (paramDate && DateTime.fromISO(paramDate).isValid) {
      const parsed = DateTime.fromISO(paramDate);
      const view = this._viewSettingsStore.view;

      if (view === ViewTable.Month) {
        date = parsed.plus({ days: 15 }).startOf('day');
      } else {
        date = parsed.startOf('week');
      }
    } else {
      date = DateTime.local().startOf(this._viewSettingsStore.view);
    }

    this.setCurrentDate(date);
  }

  destroy(): void {
    //
  }
}
